/**
 * Canonical Closing Package ControlTask types (IMP-001G / IMP-001H).
 * Mirrors backend closingPackageTaskTypes.ts — do not invent literals elsewhere.
 */

export const CLOSING_PACKAGE_TASK_TYPE = {
  FORM_CLOSING_PACKAGE: 'FORM_CLOSING_PACKAGE',
  SEND_SCANS_TO_CLIENT: 'SEND_SCANS_TO_CLIENT',
  WAIT_CLIENT_APPROVAL: 'WAIT_CLIENT_APPROVAL',
  REVISE_CLOSING_PACKAGE: 'REVISE_CLOSING_PACKAGE',
  PREPARE_ORIGINALS: 'PREPARE_ORIGINALS',
  SEND_ORIGINALS: 'SEND_ORIGINALS',
  CONFIRM_CLIENT_RECEIPT: 'CONFIRM_CLIENT_RECEIPT',
  SEND_VIA_EDO: 'SEND_VIA_EDO',
  CONFIRM_EDO_RECEIPT: 'CONFIRM_EDO_RECEIPT',
} as const;

export type ClosingPackageTaskType =
  (typeof CLOSING_PACKAGE_TASK_TYPE)[keyof typeof CLOSING_PACKAGE_TASK_TYPE];

/** Read-side aliases accepted by projection / legacy integrations. */
export const CLOSING_PACKAGE_TASK_TYPE_ALIAS = {
  SEND_CLOSING_PACKAGE_SCANS: 'SEND_CLOSING_PACKAGE_SCANS',
  SEND_CLOSING_PACKAGE_ORIGINALS: 'SEND_CLOSING_PACKAGE_ORIGINALS',
} as const;

export type ClosingPackageTaskTypeAlias =
  (typeof CLOSING_PACKAGE_TASK_TYPE_ALIAS)[keyof typeof CLOSING_PACKAGE_TASK_TYPE_ALIAS];

export type ClosingPackageTaskTypeOrAlias = ClosingPackageTaskType | ClosingPackageTaskTypeAlias;

export const CLOSING_PACKAGE_TASK_TYPE_CANONICAL_MAP: Readonly<
  Record<ClosingPackageTaskTypeOrAlias, ClosingPackageTaskType>
> = {
  [CLOSING_PACKAGE_TASK_TYPE.FORM_CLOSING_PACKAGE]: CLOSING_PACKAGE_TASK_TYPE.FORM_CLOSING_PACKAGE,
  [CLOSING_PACKAGE_TASK_TYPE.SEND_SCANS_TO_CLIENT]: CLOSING_PACKAGE_TASK_TYPE.SEND_SCANS_TO_CLIENT,
  [CLOSING_PACKAGE_TASK_TYPE.WAIT_CLIENT_APPROVAL]: CLOSING_PACKAGE_TASK_TYPE.WAIT_CLIENT_APPROVAL,
  [CLOSING_PACKAGE_TASK_TYPE.REVISE_CLOSING_PACKAGE]: CLOSING_PACKAGE_TASK_TYPE.REVISE_CLOSING_PACKAGE,
  [CLOSING_PACKAGE_TASK_TYPE.PREPARE_ORIGINALS]: CLOSING_PACKAGE_TASK_TYPE.PREPARE_ORIGINALS,
  [CLOSING_PACKAGE_TASK_TYPE.SEND_ORIGINALS]: CLOSING_PACKAGE_TASK_TYPE.SEND_ORIGINALS,
  [CLOSING_PACKAGE_TASK_TYPE.CONFIRM_CLIENT_RECEIPT]: CLOSING_PACKAGE_TASK_TYPE.CONFIRM_CLIENT_RECEIPT,
  [CLOSING_PACKAGE_TASK_TYPE.SEND_VIA_EDO]: CLOSING_PACKAGE_TASK_TYPE.SEND_VIA_EDO,
  [CLOSING_PACKAGE_TASK_TYPE.CONFIRM_EDO_RECEIPT]: CLOSING_PACKAGE_TASK_TYPE.CONFIRM_EDO_RECEIPT,
  [CLOSING_PACKAGE_TASK_TYPE_ALIAS.SEND_CLOSING_PACKAGE_SCANS]:
    CLOSING_PACKAGE_TASK_TYPE.SEND_SCANS_TO_CLIENT,
  [CLOSING_PACKAGE_TASK_TYPE_ALIAS.SEND_CLOSING_PACKAGE_ORIGINALS]:
    CLOSING_PACKAGE_TASK_TYPE.SEND_ORIGINALS,
};

export const CLOSING_PACKAGE_CANONICAL_TASK_TYPES: ReadonlyArray<ClosingPackageTaskType> =
  Object.values(CLOSING_PACKAGE_TASK_TYPE);

export function normalizeClosingPackageTaskTypeKey(value: string | null | undefined): string {
  return String(value || '').trim().toUpperCase();
}

export function resolveClosingPackageCanonicalTaskType(
  value: string | null | undefined
): ClosingPackageTaskType | null {
  const key = normalizeClosingPackageTaskTypeKey(value);
  if (!key) return null;

  const fromMap =
    CLOSING_PACKAGE_TASK_TYPE_CANONICAL_MAP[key as ClosingPackageTaskTypeOrAlias];
  if (fromMap) return fromMap;

  if ((CLOSING_PACKAGE_CANONICAL_TASK_TYPES as ReadonlyArray<string>).includes(key)) {
    return key as ClosingPackageTaskType;
  }

  return null;
}

export function isClosingPackageTaskType(value: string | null | undefined): boolean {
  return resolveClosingPackageCanonicalTaskType(value) != null;
}
